import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, ReferenceLine, Legend } from 'recharts';
import CloudInstanceCounter from '../components/admin/CloudInstanceCounter';

interface OrphanSummary {
  total: number;
  stale_builder: number;
  active_builder: number;
  user_orphan: number;
  pre_validator: number;
  unknown: number;
  estimated_hourly_waste: number;
}

interface AdminAnalytics {
  period_days: number;
  users: {
    total: number;
    active: number;
    new: number;
  };
  usage: {
    total_compute_hours: number;
    total_cost: number;
    active_users: number;
    provider_breakdown: Array<{
      provider: string;
      usage: number;
      cost: number;
    }>;
  };
  payments: {
    total: number;
    recent: number;
  };
  orphans?: OrphanSummary;
}

interface User {
  id: string;
  email: string;
  role: string;
  account_status: string;
  created_at: string;
  last_login: string | null;
  total_compute_hours: number;
  total_spend: number;
  current_month_cost: number;
  current_month_hours: number;
  has_stripe_customer: boolean;
  subscription_status: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

export default function AdminDashboardPage() {
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
  const [recentUsers, setRecentUsers] = useState<User[]>([]);
  const [failedLaunches, setFailedLaunches] = useState<any[]>([]);
  const [launchAnalytics, setLaunchAnalytics] = useState<any>(null);
  const [expandedFailure, setExpandedFailure] = useState<number | null>(null);
  const [launchDays, setLaunchDays] = useState(30);
  const [failureFilter, setFailureFilter] = useState<{provider?: string; region?: string; category?: string}>({});
  const [showAffectedUsers, setShowAffectedUsers] = useState(false);
  const [regionSort, setRegionSort] = useState<{field: 'region' | 'rate' | 'failures' | 'launches'; dir: 'asc' | 'desc'}>({field: 'failures', dir: 'desc'});
  const [instanceSort, setInstanceSort] = useState<{field: 'instance_type' | 'rate' | 'failures'; dir: 'asc' | 'desc'}>({field: 'failures', dir: 'desc'});
  const [quotaAlerts, setQuotaAlerts] = useState<any[]>([]);
  const [azSuccessRates, setAzSuccessRates] = useState<any[]>([]);
  const [researchGaps, setResearchGaps] = useState<any[]>([]);
  const [researchGapsTotal, setResearchGapsTotal] = useState<number>(0);
  // Weekly summary admin workflow. ISO week is resolved from the feed
  // meta endpoint so this dashboard always reflects the newest ingested
  // week, not a hardcoded value.
  const [weeklySummaryStatus, setWeeklySummaryStatus] = useState<any | null>(null);
  const [weeklySummaryWeek, setWeeklySummaryWeek] = useState<string | null>(null);
  const [weeklySummaryBusy, setWeeklySummaryBusy] = useState<'idle' | 'generating' | 'publishing'>('idle');
  const [weeklySummaryError, setWeeklySummaryError] = useState<string | null>(null);
  // Aggregated user-submitted dataset requests (last 90d).
  const [datasetRequests, setDatasetRequests] = useState<any[]>([]);
  const [datasetRequestsTotals, setDatasetRequestsTotals] = useState<{total_requests: number; total_distinct_users: number}>({total_requests: 0, total_distinct_users: 0});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orphanBannerDismissed, setOrphanBannerDismissed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    fetchAdminData();
  }, [navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      // Fetch analytics overview
      const analyticsResponse = await axios.get(`${getApiUrl()}/admin/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Fetch recent users
      const usersResponse = await axios.get(`${getApiUrl()}/admin/users?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAnalytics(analyticsResponse.data);
      setRecentUsers(usersResponse.data.users);
      setError(null);

      await fetchLaunchData(token || undefined);
    } catch (err: any) {
      console.error('Error fetching admin data:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else if (err.response?.status === 401) {
        navigate('/login');
      } else {
        setError('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLaunchData = async (token?: string) => {
    const t = token || localStorage.getItem('authToken');
    if (!t) return;
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (failureFilter.provider) params.set('provider', failureFilter.provider);
      if (failureFilter.region) params.set('region', failureFilter.region);
      if (failureFilter.category) params.set('error_category', failureFilter.category);

      const [failedResponse, analyticsResponse, alertsResponse, azRateResponse, gapsResponse, datasetReqResponse, metaResponse] = await Promise.allSettled([
        axios.get(`${getApiUrl()}/admin/failed-launches?${params}`, {
          headers: { Authorization: `Bearer ${t}` }
        }),
        axios.get(`${getApiUrl()}/admin/launch-analytics?days=${launchDays}`, {
          headers: { Authorization: `Bearer ${t}` }
        }),
        axios.get(`${getApiUrl()}/admin/quota-alerts?days=7&active_only=true&limit=20`, {
          headers: { Authorization: `Bearer ${t}` }
        }),
        axios.get(`${getApiUrl()}/admin/az-success-rates?window_min=60`, {
          headers: { Authorization: `Bearer ${t}` }
        }),
        axios.get(`${getApiUrl()}/admin/research-dataset-gaps?window_days=30`, {
          headers: { Authorization: `Bearer ${t}` }
        }),
        axios.get(`${getApiUrl()}/admin/dataset-requests?window_days=90`, {
          headers: { Authorization: `Bearer ${t}` }
        }),
        axios.get(`${getApiUrl()}/api/v1/research-insights/meta`, {
          headers: { Authorization: `Bearer ${t}` }
        }),
      ]);
      if (failedResponse.status === 'fulfilled') {
        setFailedLaunches(failedResponse.value.data.items || []);
      }
      if (analyticsResponse.status === 'fulfilled') {
        setLaunchAnalytics(analyticsResponse.value.data);
      }
      if (alertsResponse.status === 'fulfilled') {
        setQuotaAlerts(alertsResponse.value.data.items || []);
      }
      if (azRateResponse.status === 'fulfilled') {
        setAzSuccessRates(azRateResponse.value.data.items || []);
      }
      if (gapsResponse.status === 'fulfilled') {
        setResearchGaps(gapsResponse.value.data.gaps || []);
        setResearchGapsTotal(gapsResponse.value.data.total_findings_only_insights || 0);
      }
      if (datasetReqResponse.status === 'fulfilled') {
        setDatasetRequests(datasetReqResponse.value.data.tokens || []);
        setDatasetRequestsTotals({
          total_requests: datasetReqResponse.value.data.total_requests || 0,
          total_distinct_users: datasetReqResponse.value.data.total_distinct_users || 0,
        });
      }
      // Pick the freshest ingested week from feed meta — this is the one
      // the "Weekly Summary" panel will operate on. Falls back gracefully
      // if meta is missing.
      if (metaResponse.status === 'fulfilled') {
        const weeks: string[] = metaResponse.value.data.weeks || [];
        const latestWeek = weeks.length > 0 ? weeks[0] : null;
        setWeeklySummaryWeek(latestWeek);
        if (latestWeek) {
          try {
            const sumResp = await axios.get(
              `${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/${latestWeek}`,
              { headers: { Authorization: `Bearer ${t}` } },
            );
            setWeeklySummaryStatus(sumResp.data || null);
          } catch {
            setWeeklySummaryStatus(null);
          }
        }
      }
    } catch (err) {
      console.warn('Failed to fetch launch data:', err);
    }
  };

  const handleGenerateWeeklySummary = async () => {
    if (!weeklySummaryWeek) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setWeeklySummaryBusy('generating');
    setWeeklySummaryError(null);
    try {
      const resp = await axios.post(
        `${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/${weeklySummaryWeek}/generate`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 300000 },
      );
      setWeeklySummaryStatus(resp.data);
    } catch (err: any) {
      setWeeklySummaryError(err?.response?.data?.detail || err?.message || 'Generation failed');
    } finally {
      setWeeklySummaryBusy('idle');
    }
  };

  const handlePublishWeeklySummary = async () => {
    if (!weeklySummaryWeek) return;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    setWeeklySummaryBusy('publishing');
    setWeeklySummaryError(null);
    try {
      const resp = await axios.post(
        `${getApiUrl()}/api/v1/research-insights/admin/weekly-summary/${weeklySummaryWeek}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setWeeklySummaryStatus(resp.data);
    } catch (err: any) {
      setWeeklySummaryError(err?.response?.data?.detail || err?.message || 'Publish failed');
    } finally {
      setWeeklySummaryBusy('idle');
    }
  };

  // Refetch launch data when time range or filters change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLaunchData(); }, [launchDays, failureFilter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
          <div className="text-red-400">{error}</div>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-400">No analytics data available</div>
      </div>
    );
  }

  const chartData = analytics.usage.provider_breakdown.map(provider => ({
    name: provider.provider.toUpperCase(),
    usage: provider.usage,
    cost: provider.cost
  }));

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
            <p className="text-gray-400">
              Platform overview and user management
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/admin/users')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Manage Users
            </button>
            <button
              onClick={() => navigate('/admin/gpu-quotas')}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              GPU Quotas
            </button>
            <button
              onClick={() => navigate('/admin/ami-builder')}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Image Template Management
            </button>
            <button
              onClick={() => navigate('/admin/cross-cloud-costs')}
              className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Cross-Cloud Costs
            </button>
            <button
              onClick={fetchAdminData}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Cloud Instance Counter */}
        <CloudInstanceCounter />

        {/* Orphan Instance Status */}
        {analytics.orphans && analytics.orphans.total > 0 && !orphanBannerDismissed ? (
          <div className="mb-6 bg-amber-900/30 border border-amber-700/50 rounded-xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div>
                <p className="text-amber-200 font-medium text-sm">
                  {analytics.orphans.total} orphan instance{analytics.orphans.total !== 1 ? 's' : ''} detected
                </p>
                <p className="text-amber-400/70 text-xs mt-0.5">
                  {[
                    analytics.orphans.stale_builder > 0 && `${analytics.orphans.stale_builder} stale builder${analytics.orphans.stale_builder !== 1 ? 's' : ''}`,
                    analytics.orphans.user_orphan > 0 && `${analytics.orphans.user_orphan} user orphan${analytics.orphans.user_orphan !== 1 ? 's' : ''}`,
                    analytics.orphans.active_builder > 0 && `${analytics.orphans.active_builder} active builder${analytics.orphans.active_builder !== 1 ? 's' : ''}`,
                    analytics.orphans.pre_validator > 0 && `${analytics.orphans.pre_validator} pre-validator${analytics.orphans.pre_validator !== 1 ? 's' : ''}`,
                  ].filter(Boolean).join(', ')}
                  {analytics.orphans.estimated_hourly_waste > 0 && ` — ~$${analytics.orphans.estimated_hourly_waste.toFixed(2)}/hr waste`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => navigate('/admin/orphan-instances')}
                className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-medium transition-colors"
              >
                Review
              </button>
              <button
                onClick={() => setOrphanBannerDismissed(true)}
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg text-xs font-medium transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        ) : analytics.orphans ? (
          <div className="mb-6 flex items-center gap-2 text-xs text-gray-500">
            <span className="inline-block w-2 h-2 rounded-full bg-green-500" />
            <span>Cloud instance reconciliation: no orphans detected — DB matches AWS/GCP</span>
          </div>
        ) : (
          <div className="mb-6 flex items-center gap-2 text-xs text-gray-500">
            <span className="inline-block w-2 h-2 rounded-full bg-gray-600" />
            <span>Orphan scan pending — next reconciliation cycle will populate results</span>
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Users</p>
                <p className="text-3xl font-bold text-white">{analytics.users.total}</p>
              </div>
              <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5 0a8.5 8.5 0 00-6.5-6.499M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1z" />
                </svg>
              </div>
            </div>
            <div className="mt-2">
              <span className="text-sm text-green-400">+{analytics.users.new} new this month</span>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Active Users</p>
                <p className="text-3xl font-bold text-white">{analytics.users.active}</p>
              </div>
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="mt-2">
              <span className="text-sm text-gray-400">{((analytics.users.active / analytics.users.total) * 100).toFixed(1)}% of total</span>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Revenue</p>
                <p className="text-3xl font-bold text-white">${analytics.usage.total_cost.toFixed(2)}</p>
              </div>
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
            <div className="mt-2">
              <span className="text-sm text-gray-400">Last 30 days</span>
            </div>
          </div>

          <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Compute Hours</p>
                <p className="text-3xl font-bold text-white">{analytics.usage.total_compute_hours.toFixed(1)}</p>
              </div>
              <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
            <div className="mt-2">
              <span className="text-sm text-gray-400">{analytics.usage.active_users} active users</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Provider Usage Chart */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
            <h3 className="text-lg font-bold text-white mb-6">Usage by Cloud Provider</h3>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1F2937', 
                      border: '1px solid #374151',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="cost" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-300 text-gray-400">
                No usage data available
              </div>
            )}
          </div>

          {/* Provider Distribution Pie Chart */}
          <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
            <h3 className="text-lg font-bold text-white mb-6">Cost Distribution</h3>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: { name: string; percent: number }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="cost"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-300 text-gray-400">
                No usage data available
              </div>
            )}
          </div>
        </div>

        {/* Recent Users */}
        <div className="bg-gray-900 rounded-xl border border-gray-800">
          <div className="p-6 border-b border-gray-800">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Recent Users</h2>
              <button
                onClick={() => navigate('/admin/users')}
                className="text-blue-400 hover:text-blue-300 text-sm font-medium"
              >
                View All Users →
              </button>
            </div>
          </div>
          
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left border-b border-gray-800">
                    <th className="pb-3 text-sm font-medium text-gray-300">User</th>
                    <th className="pb-3 text-sm font-medium text-gray-300">Role</th>
                    <th className="pb-3 text-sm font-medium text-gray-300">Status</th>
                    <th className="pb-3 text-sm font-medium text-gray-300">This Month</th>
                    <th className="pb-3 text-sm font-medium text-gray-300">Total Spend</th>
                    <th className="pb-3 text-sm font-medium text-gray-300">Last Login</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {recentUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-800/30">
                      <td className="py-4">
                        <div>
                          <div className="text-sm font-medium text-white">{user.email}</div>
                          <div className="text-xs text-gray-400">ID: {user.id}</div>
                        </div>
                      </td>
                      <td className="py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.role === 'admin' 
                            ? 'bg-purple-500/20 text-purple-400'
                            : user.role === 'enterprise'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.account_status === 'active'
                            ? 'bg-green-500/20 text-green-400'
                            : user.account_status === 'suspended'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {user.account_status}
                        </span>
                      </td>
                      <td className="py-4 text-sm text-gray-300">
                        <div>${user.current_month_cost.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">{user.current_month_hours.toFixed(1)}h</div>
                      </td>
                      <td className="py-4 text-sm text-gray-300">
                        ${user.total_spend.toFixed(2)}
                      </td>
                      <td className="py-4 text-sm text-gray-400">
                        {user.last_login 
                          ? new Date(user.last_login).toLocaleDateString()
                          : 'Never'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Launch Health Analytics */}
        <div className="bg-gray-900 rounded-xl border border-gray-700 mt-8">
          <div className="p-6 border-b border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">Launch Health</h2>
              <p className="text-sm text-gray-400 mt-1">
                {launchAnalytics ? `${launchAnalytics.summary.total_launches} launches tracked` : 'Loading...'}
              </p>
            </div>
            <div className="flex gap-1 bg-gray-800 rounded-lg p-1">
              {[{label: '24h', value: 1}, {label: '7d', value: 7}, {label: '30d', value: 30}, {label: '90d', value: 90}].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setLaunchDays(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    launchDays === opt.value
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {launchAnalytics && (
            <div className="p-6">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-white">{launchAnalytics.summary.total_launches}</div>
                  <div className="text-xs text-gray-400 mt-1">Total Launches</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className={`text-2xl font-bold ${
                    launchAnalytics.summary.success_rate >= 90 ? 'text-green-400'
                    : launchAnalytics.summary.success_rate >= 70 ? 'text-yellow-400'
                    : 'text-red-400'
                  }`}>
                    {launchAnalytics.summary.success_rate}%
                  </div>
                  <div className="text-xs text-gray-400 mt-1">Success Rate</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-amber-400">{launchAnalytics.summary.capacity_failures}</div>
                  <div className="text-xs text-gray-400 mt-1">Capacity Failures</div>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-red-400">{launchAnalytics.summary.other_failures}</div>
                  <div className="text-xs text-gray-400 mt-1">Other Failures</div>
                </div>
              </div>

              {/* Time series chart with capacity-event overlay */}
              {launchAnalytics.time_series?.length > 1 && (() => {
                // Bucket capacity events by (date, provider) so we draw at most one line per day+provider
                const eventDateProviders = new Set<string>();
                const buckets: Array<{date: string; provider: string; count: number; tooltipRows: any[]}> = [];
                for (const ev of (launchAnalytics.recent_capacity_events ?? [])) {
                  if (!ev.occurred_at) continue;
                  const d = new Date(ev.occurred_at);
                  const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                  const key = `${dateKey}|${ev.provider}`;
                  if (!eventDateProviders.has(key)) {
                    eventDateProviders.add(key);
                    buckets.push({date: dateKey, provider: ev.provider, count: 1, tooltipRows: [ev]});
                  } else {
                    const bucket = buckets.find(b => b.date === dateKey && b.provider === ev.provider);
                    if (bucket) {
                      bucket.count += 1;
                      if (bucket.tooltipRows.length < 3) bucket.tooltipRows.push(ev);
                    }
                  }
                }
                const tsDates = new Set(launchAnalytics.time_series.map((t: any) => t.date));
                const visibleBuckets = buckets.filter(b => tsDates.has(b.date));
                const providerColor = (p: string) =>
                  p === 'aws' ? '#F59E0B' : p === 'gcp' ? '#60A5FA' : p === 'azure' ? '#22D3EE' : '#A78BFA';
                return (
                  <div className="mb-6">
                    <div className="flex items-baseline justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-300">Daily Launch Activity</h3>
                      {visibleBuckets.length > 0 && (
                        <div className="flex items-center gap-3 text-[11px] text-gray-400">
                          <span>Capacity events:</span>
                          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{backgroundColor: '#F59E0B'}} /> AWS</span>
                          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{backgroundColor: '#60A5FA'}} /> GCP</span>
                          <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full" style={{backgroundColor: '#22D3EE'}} /> Azure</span>
                        </div>
                      )}
                    </div>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={launchAnalytics.time_series} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                          <XAxis
                            dataKey="date"
                            tick={{ fill: '#9CA3AF', fontSize: 11 }}
                            tickFormatter={(d: string) => new Date(d + 'T00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          />
                          <YAxis tick={{ fill: '#9CA3AF', fontSize: 11 }} allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: 8, fontSize: 12 }}
                            labelStyle={{ color: '#D1D5DB' }}
                            formatter={(value: any, name: any) => [value, name]}
                            labelFormatter={(label: string) => {
                              const onDate = visibleBuckets.filter(b => b.date === label);
                              if (onDate.length === 0) return label;
                              const lines = onDate.flatMap(b => b.tooltipRows.map((ev: any) =>
                                `• ${ev.provider?.toUpperCase()} ${ev.zone || ''} ${ev.instance_type || ''} ${ev.error_code ? `(${ev.error_code})` : ''}`.trim()
                              ));
                              return `${label}\nCapacity events:\n${lines.join('\n')}`;
                            }}
                          />
                          <Bar dataKey="launches" name="Launches" fill="#3B82F6" radius={[2, 2, 0, 0]} />
                          <Bar dataKey="failures" name="Failures" fill="#EF4444" radius={[2, 2, 0, 0]} />
                          {visibleBuckets.map((b, i) => (
                            <ReferenceLine
                              key={`${b.date}-${b.provider}-${i}`}
                              x={b.date}
                              stroke={providerColor(b.provider)}
                              strokeWidth={2}
                              strokeDasharray="3 3"
                              ifOverflow="extendDomain"
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })()}

              {/* Error categories + region breakdown side by side */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* By error category — clickable to filter failed launches */}
                {launchAnalytics.by_error_category.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-300 mb-3">Failures by Category</h3>
                    <div className="space-y-2">
                      {launchAnalytics.by_error_category.map((cat: any) => (
                        <button
                          key={cat.category}
                          onClick={() => setFailureFilter(prev =>
                            prev.category === cat.category ? {...prev, category: undefined} : {...prev, category: cat.category}
                          )}
                          className={`w-full flex items-center justify-between rounded px-3 py-2 text-left transition-colors ${
                            failureFilter.category === cat.category
                              ? 'bg-blue-900/40 border border-blue-700/50'
                              : 'bg-gray-800/30 hover:bg-gray-800/50'
                          }`}
                        >
                          <span className="text-sm text-gray-300 capitalize">{cat.category.replace(/_/g, ' ')}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-mono text-gray-200">{cat.count}</span>
                            {cat.last_seen && (
                              <span className="text-xs text-gray-500">
                                {new Date(cat.last_seen).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* By region — sortable + clickable to filter */}
                {launchAnalytics.by_region.length > 0 && (() => {
                  const sortedRegions = [...launchAnalytics.by_region].sort((a: any, b: any) => {
                    const f = regionSort.field;
                    const av = a[f] ?? 0;
                    const bv = b[f] ?? 0;
                    const cmp = typeof av === 'string' ? av.localeCompare(bv) : (av - bv);
                    return regionSort.dir === 'asc' ? cmp : -cmp;
                  });
                  const toggle = (field: typeof regionSort.field) =>
                    setRegionSort(prev => prev.field === field
                      ? {field, dir: prev.dir === 'asc' ? 'desc' : 'asc'}
                      : {field, dir: field === 'region' ? 'asc' : 'desc'});
                  const arrow = (field: typeof regionSort.field) =>
                    regionSort.field === field ? (regionSort.dir === 'asc' ? ' ↑' : ' ↓') : '';
                  return (
                    <div>
                      <h3 className="text-sm font-medium text-gray-300 mb-3">By Region</h3>
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-gray-500 px-3 py-1">
                        <button onClick={() => toggle('region')} className="hover:text-gray-300">Region{arrow('region')}</button>
                        <div className="flex items-center gap-3">
                          <button onClick={() => toggle('rate')} className="hover:text-gray-300">Rate{arrow('rate')}</button>
                          <button onClick={() => toggle('failures')} className="hover:text-gray-300">Failures{arrow('failures')}</button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {sortedRegions.map((r: any) => (
                          <button
                            key={r.region}
                            onClick={() => setFailureFilter(prev =>
                              prev.region === r.region ? {...prev, region: undefined} : {...prev, region: r.region}
                            )}
                            className={`w-full flex items-center justify-between rounded px-3 py-2 text-left transition-colors ${
                              failureFilter.region === r.region
                                ? 'bg-blue-900/40 border border-blue-700/50'
                                : 'bg-gray-800/30 hover:bg-gray-800/50'
                            }`}
                          >
                            <span className="text-sm text-gray-300 font-mono">{r.region}</span>
                            <div className="flex items-center gap-3">
                              <span className={`text-sm font-mono ${
                                r.rate >= 90 ? 'text-green-400' : r.rate >= 70 ? 'text-yellow-400' : 'text-red-400'
                              }`}>
                                {r.rate}%
                              </span>
                              <span className="text-xs text-gray-500">{r.failures}/{r.launches} failed</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Failing instance types — sortable */}
              {launchAnalytics.by_instance_type.length > 0 && (() => {
                const sortedInstances = [...launchAnalytics.by_instance_type].sort((a: any, b: any) => {
                  const f = instanceSort.field;
                  const av = a[f] ?? 0;
                  const bv = b[f] ?? 0;
                  const cmp = typeof av === 'string' ? av.localeCompare(bv) : (av - bv);
                  return instanceSort.dir === 'asc' ? cmp : -cmp;
                }).slice(0, 8);
                const toggle = (field: typeof instanceSort.field) =>
                  setInstanceSort(prev => prev.field === field
                    ? {field, dir: prev.dir === 'asc' ? 'desc' : 'asc'}
                    : {field, dir: field === 'instance_type' ? 'asc' : 'desc'});
                const arrow = (field: typeof instanceSort.field) =>
                  instanceSort.field === field ? (instanceSort.dir === 'asc' ? ' ↑' : ' ↓') : '';
                return (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-300 mb-3">Failing Instance Types</h3>
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-gray-500 px-3 py-1">
                      <button onClick={() => toggle('instance_type')} className="hover:text-gray-300">Instance{arrow('instance_type')}</button>
                      <div className="flex items-center gap-3">
                        <button onClick={() => toggle('rate')} className="hover:text-gray-300">Rate{arrow('rate')}</button>
                        <button onClick={() => toggle('failures')} className="hover:text-gray-300">Failures{arrow('failures')}</button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {sortedInstances.map((t: any) => (
                        <div key={t.instance_type} className="flex items-center justify-between bg-gray-800/30 rounded px-3 py-2">
                          <span className="text-sm text-gray-300 font-mono">{t.instance_type}</span>
                          <div className="flex items-center gap-3">
                            <span className={`text-sm font-mono ${
                              t.rate >= 90 ? 'text-green-400' : t.rate >= 70 ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {t.rate}%
                            </span>
                            <span className="text-xs text-gray-500">{t.failures} failed</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Quota utilization alerts (80% / 90% / 95%) */}
              {quotaAlerts.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">
                    Quota Utilization Alerts <span className="text-gray-500 text-xs">(7d, active)</span>
                  </h3>
                  <div className="space-y-2">
                    {quotaAlerts.slice(0, 10).map((a: any) => {
                      const levelColor =
                        a.alert_level === 'emergency' ? 'bg-red-900/40 border-red-700/50 text-red-300'
                        : a.alert_level === 'critical' ? 'bg-orange-900/40 border-orange-700/50 text-orange-300'
                        : 'bg-amber-900/30 border-amber-700/40 text-amber-300';
                      return (
                        <div key={a.id} className={`flex items-center justify-between rounded border px-3 py-2 ${levelColor}`}>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium capitalize">
                              {a.alert_level} · {a.provider?.toUpperCase()} {a.region} · {a.instance_family}
                            </div>
                            <div className="text-xs text-gray-400 truncate">{a.message}</div>
                          </div>
                          <div className="flex items-center gap-3 ml-4 shrink-0">
                            <span className="text-sm font-mono">
                              {a.utilization_percent != null ? `${a.utilization_percent}%` : '—'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {a.current_usage ?? '?'}/{a.quota_limit ?? '?'}
                            </span>
                            {a.created_at && (
                              <span className="text-xs text-gray-500">
                                {new Date(a.created_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Weekly editorial summary — generate + publish workflow */}
              {weeklySummaryWeek && (
                <div className="mb-6 rounded-lg border border-indigo-500/20 bg-indigo-500/5 p-4">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-sm font-medium text-gray-200">
                      Weekly Summary
                      <span className="ml-2 text-xs font-mono text-indigo-300">{weeklySummaryWeek}</span>
                    </h3>
                    <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                      weeklySummaryStatus?.status === 'published'
                        ? 'bg-emerald-500/20 text-emerald-300'
                        : weeklySummaryStatus?.status === 'draft'
                          ? 'bg-amber-500/20 text-amber-300'
                          : 'bg-gray-700 text-gray-400'
                    }`}>
                      {weeklySummaryStatus?.status || 'not generated'}
                    </span>
                  </div>
                  <p className="mb-3 text-xs text-gray-500">
                    Opus-generated editorial recap shown at the top of the user Research Feed.
                    Re-generate any time; publishing flips the live surface to this version.
                  </p>
                  {weeklySummaryStatus && (
                    <div className="mb-3 rounded border border-gray-800 bg-black/30 p-3 text-xs text-gray-400 space-y-1">
                      <div><span className="text-gray-500">Model:</span> <span className="font-mono">{weeklySummaryStatus.model_used}</span></div>
                      <div><span className="text-gray-500">Papers analyzed:</span> {weeklySummaryStatus.input_paper_count}</div>
                      <div><span className="text-gray-500">Generated:</span> {weeklySummaryStatus.generated_at ? new Date(weeklySummaryStatus.generated_at).toLocaleString() : '—'}</div>
                      <div><span className="text-gray-500">Published:</span> {weeklySummaryStatus.published_at ? new Date(weeklySummaryStatus.published_at).toLocaleString() : 'not yet'}</div>
                      <div className="pt-2 border-t border-gray-800/60">
                        <div className="text-gray-500">Sections:</div>
                        <div className="text-gray-300">
                          {(weeklySummaryStatus.content?.excited_to_try?.length || 0)} excited ·
                          {' '}{(weeklySummaryStatus.content?.still_early?.length || 0)} early ·
                          {' '}{(weeklySummaryStatus.content?.new_datasets?.length || 0)} datasets ·
                          {' '}{(weeklySummaryStatus.content?.themes?.length || 0)} themes
                        </div>
                      </div>
                      {weeklySummaryStatus.content?.intro && (
                        <div className="pt-2 border-t border-gray-800/60">
                          <div className="text-gray-500 mb-1">Intro preview:</div>
                          <div className="text-gray-300 italic line-clamp-3">{weeklySummaryStatus.content.intro}</div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleGenerateWeeklySummary}
                      disabled={weeklySummaryBusy !== 'idle'}
                      className="inline-flex items-center gap-1.5 rounded border border-indigo-500/40 bg-indigo-500/10 px-3 py-1.5 text-xs font-medium text-indigo-200 hover:bg-indigo-500/20 disabled:opacity-50"
                    >
                      {weeklySummaryBusy === 'generating' ? 'Generating… (~2 min)' : (weeklySummaryStatus ? 'Re-generate draft' : 'Generate draft')}
                    </button>
                    {weeklySummaryStatus && (
                      <button
                        type="button"
                        onClick={handlePublishWeeklySummary}
                        disabled={weeklySummaryBusy !== 'idle'}
                        className="inline-flex items-center gap-1.5 rounded bg-white px-3 py-1.5 text-xs font-medium text-black hover:bg-gray-200 disabled:opacity-50"
                      >
                        {weeklySummaryBusy === 'publishing'
                          ? 'Publishing…'
                          : weeklySummaryStatus.status === 'published'
                            ? 'Re-publish current'
                            : 'Publish to feed'}
                      </button>
                    )}
                  </div>
                  {weeklySummaryError && (
                    <div className="mt-2 text-xs text-red-400">{weeklySummaryError}</div>
                  )}
                </div>
              )}

              {/* User dataset requests (active demand — last 90 days) */}
              {datasetRequests.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-300 mb-1">
                    Dataset Requests from Users
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Users who clicked "Request this dataset" on a research-feed card. Active
                    demand signal paired with the passive gap aggregation below.
                    ({datasetRequestsTotals.total_requests} total requests from {datasetRequestsTotals.total_distinct_users} distinct users)
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b border-gray-800">
                          <th className="pb-2 text-xs text-gray-400">Dataset</th>
                          <th className="pb-2 text-xs text-gray-400 text-right">Requests</th>
                          <th className="pb-2 text-xs text-gray-400 text-right">Users</th>
                          <th className="pb-2 text-xs text-gray-400 text-right">Papers</th>
                          <th className="pb-2 text-xs text-gray-400">Source papers</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/50">
                        {datasetRequests.slice(0, 12).map((t: any) => (
                          <tr key={t.dataset_token} className="text-gray-300">
                            <td className="py-2 text-xs font-mono text-sky-300">{t.dataset_token}</td>
                            <td className={`py-2 text-xs text-right font-mono ${
                              t.request_count >= 10 ? 'text-amber-400'
                              : t.request_count >= 3 ? 'text-yellow-300'
                              : 'text-gray-400'
                            }`}>{t.request_count}</td>
                            <td className="py-2 text-xs text-right font-mono text-gray-400">{t.distinct_users}</td>
                            <td className="py-2 text-xs text-right font-mono text-gray-400">{t.paper_count}</td>
                            <td className="py-2 text-xs text-gray-500">
                              {(t.papers || []).slice(0, 2).map((p: any) => (
                                <a
                                  key={p.public_id}
                                  href={p.paper_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block max-w-md truncate hover:text-gray-300"
                                  title={p.paper_title}
                                >
                                  {p.paper_title}
                                </a>
                              ))}
                              {t.paper_count > 2 && (
                                <span className="text-gray-600">+ {t.paper_count - 2} more</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Research-feed dataset gap signals (last 30 days) */}
              {researchGaps.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-300 mb-1">
                    Research Feed: Dataset Gaps
                  </h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Data categories papers in the last 30 days needed but PropAlgos doesn't stock.
                    Signal for where to prioritize data-acquisition work.
                    ({researchGapsTotal} findings-only insights analyzed)
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b border-gray-800">
                          <th className="pb-2 text-xs text-gray-400">Dataset gap</th>
                          <th className="pb-2 text-xs text-gray-400 text-right">Papers</th>
                          <th className="pb-2 text-xs text-gray-400">Latest examples</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/50">
                        {researchGaps.slice(0, 10).map((g: any) => (
                          <tr key={g.dataset_token} className="text-gray-300">
                            <td className="py-2 text-xs font-mono">{g.dataset_token}</td>
                            <td className={`py-2 text-xs text-right font-mono ${
                              g.paper_count >= 5 ? 'text-amber-400'
                              : g.paper_count >= 3 ? 'text-yellow-300'
                              : 'text-gray-400'
                            }`}>
                              {g.paper_count}
                            </td>
                            <td className="py-2 text-xs text-gray-500">
                              {(g.papers || []).slice(0, 2).map((p: any) => (
                                <a
                                  key={p.public_id}
                                  href={p.paper_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block max-w-md truncate hover:text-gray-300"
                                  title={p.paper_title}
                                >
                                  {p.paper_title}
                                </a>
                              ))}
                              {g.paper_count > 2 && (
                                <span className="text-gray-600">
                                  + {g.paper_count - 2} more
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* AZ success-rate (rolling 60 min) */}
              {azSuccessRates.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-sm font-medium text-gray-300 mb-3">
                    AZ Success Rate <span className="text-gray-500 text-xs">(last 60 min, min 1 launch)</span>
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b border-gray-800">
                          <th className="pb-2 text-xs text-gray-400">Provider</th>
                          <th className="pb-2 text-xs text-gray-400">Region</th>
                          <th className="pb-2 text-xs text-gray-400">AZ</th>
                          <th className="pb-2 text-xs text-gray-400 text-right">Launches</th>
                          <th className="pb-2 text-xs text-gray-400 text-right">Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/50">
                        {azSuccessRates.slice(0, 15).map((az: any, i: number) => (
                          <tr key={i} className="text-gray-300">
                            <td className="py-1.5 text-xs">{az.provider?.toUpperCase()}</td>
                            <td className="py-1.5 text-xs font-mono">{az.region}</td>
                            <td className="py-1.5 text-xs font-mono">{az.availability_zone}</td>
                            <td className="py-1.5 text-xs text-right text-gray-400">{az.launches}</td>
                            <td className={`py-1.5 text-xs text-right font-mono ${
                              az.success_rate >= 90 ? 'text-green-400'
                              : az.success_rate >= 70 ? 'text-yellow-400'
                              : 'text-red-400'
                            }`}>
                              {az.success_rate}%
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Recent capacity events */}
              {launchAnalytics.recent_capacity_events.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-300 mb-3">Recent Capacity Events (72h)</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left border-b border-gray-800">
                          <th className="pb-2 text-xs text-gray-400">Time</th>
                          <th className="pb-2 text-xs text-gray-400">Provider</th>
                          <th className="pb-2 text-xs text-gray-400">Zone</th>
                          <th className="pb-2 text-xs text-gray-400">GPU</th>
                          <th className="pb-2 text-xs text-gray-400">Instance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/50">
                        {launchAnalytics.recent_capacity_events.slice(0, 10).map((ev: any, i: number) => (
                          <tr key={i} className="text-gray-300">
                            <td className="py-1.5 text-xs text-gray-400">
                              {ev.occurred_at ? new Date(ev.occurred_at).toLocaleString() : '—'}
                            </td>
                            <td className="py-1.5">
                              <span className={`text-xs font-semibold ${
                                ev.provider === 'aws' ? 'text-orange-400' : ev.provider === 'gcp' ? 'text-blue-400' : 'text-purple-400'
                              }`}>
                                {ev.provider?.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-1.5 font-mono text-xs">{ev.zone}</td>
                            <td className="py-1.5 text-xs">{ev.gpu_type}</td>
                            <td className="py-1.5 font-mono text-xs">{ev.instance_type}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Active filter indicator */}
        {(failureFilter.category || failureFilter.region || failureFilter.provider) && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-400">Filtering failures by:</span>
            {failureFilter.category && (
              <button
                onClick={() => setFailureFilter(prev => ({...prev, category: undefined}))}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-900/40 text-blue-300 text-xs rounded-full border border-blue-700/50"
              >
                {failureFilter.category.replace(/_/g, ' ')}
                <span className="text-blue-400/70 ml-0.5">&times;</span>
              </button>
            )}
            {failureFilter.region && (
              <button
                onClick={() => setFailureFilter(prev => ({...prev, region: undefined}))}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-900/40 text-blue-300 text-xs rounded-full border border-blue-700/50"
              >
                {failureFilter.region}
                <span className="text-blue-400/70 ml-0.5">&times;</span>
              </button>
            )}
            {failureFilter.provider && (
              <button
                onClick={() => setFailureFilter(prev => ({...prev, provider: undefined}))}
                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-900/40 text-blue-300 text-xs rounded-full border border-blue-700/50"
              >
                {failureFilter.provider.toUpperCase()}
                <span className="text-blue-400/70 ml-0.5">&times;</span>
              </button>
            )}
            <button
              onClick={() => setFailureFilter({})}
              className="text-xs text-gray-500 hover:text-gray-300"
            >
              Clear all
            </button>
            <button
              onClick={() => setShowAffectedUsers(prev => !prev)}
              className="ml-auto text-xs px-2 py-1 bg-gray-800/50 hover:bg-gray-700/50 text-gray-200 rounded border border-gray-700/50"
            >
              {showAffectedUsers ? 'Hide' : 'View'} affected users
            </button>
          </div>
        )}

        {/* Affected users drill-down — groups currently filtered failures by user_email */}
        {showAffectedUsers && (failureFilter.category || failureFilter.region || failureFilter.provider) && (() => {
          type UserStats = { email: string; user_id: string | number | null; count: number; lastFailure: string | null };
          const byUser = new Map<string, UserStats>();
          for (const fl of failedLaunches) {
            const email = fl.user_email || fl.user_name || 'unknown';
            const ex = byUser.get(email);
            if (!ex) {
              byUser.set(email, {
                email,
                user_id: fl.user_id ?? null,
                count: 1,
                lastFailure: fl.created_at ?? null,
              });
            } else {
              ex.count += 1;
              if (fl.created_at && (!ex.lastFailure || fl.created_at > ex.lastFailure)) {
                ex.lastFailure = fl.created_at;
              }
            }
          }
          const users = Array.from(byUser.values()).sort((a, b) => b.count - a.count);
          const relative = (iso: string | null) => {
            if (!iso) return '—';
            const diffMs = Date.now() - new Date(iso).getTime();
            const mins = Math.max(1, Math.floor(diffMs / 60000));
            if (mins < 60) return `${mins}m ago`;
            const hrs = Math.floor(mins / 60);
            if (hrs < 24) return `${hrs}h ago`;
            const days = Math.floor(hrs / 24);
            return `${days}d ago`;
          };
          return (
            <div className="mt-3 bg-gray-900/70 border border-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-200">
                  Affected users <span className="text-gray-500">({users.length})</span>
                </h3>
                <span className="text-xs text-gray-500">
                  Grouped from {failedLaunches.length} filtered failure{failedLaunches.length === 1 ? '' : 's'}
                </span>
              </div>
              {users.length === 0 ? (
                <div className="text-sm text-gray-500">No affected users match the current filter.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-gray-800">
                        <th className="pb-2 text-xs text-gray-400">User</th>
                        <th className="pb-2 text-xs text-gray-400">Failures</th>
                        <th className="pb-2 text-xs text-gray-400">Last failure</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/50">
                      {users.map(u => (
                        <tr key={u.email} className="text-gray-300">
                          <td className="py-1.5">
                            <div className="text-sm text-white">{u.email}</div>
                            {u.user_id != null && <div className="text-xs text-gray-500">ID: {u.user_id}</div>}
                          </td>
                          <td className="py-1.5 font-mono text-sm text-amber-300">{u.count}</td>
                          <td className="py-1.5 text-xs text-gray-400">{relative(u.lastFailure)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {/* Failed Instance Launches */}
        {failedLaunches.length > 0 && (
          <div className="bg-gray-900 rounded-xl border border-red-900/50 mt-8">
            <div className="p-6 border-b border-red-900/30">
              <div className="flex items-center space-x-3">
                <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h2 className="text-xl font-bold text-white">Failed Instance Launches</h2>
                <span className="px-2 py-0.5 text-xs font-medium bg-red-900/50 text-red-300 rounded-full">
                  {failedLaunches.length}
                </span>
              </div>
            </div>

            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left border-b border-gray-800">
                      <th className="pb-3 text-sm font-medium text-gray-300">Time</th>
                      <th className="pb-3 text-sm font-medium text-gray-300">User</th>
                      <th className="pb-3 text-sm font-medium text-gray-300">Provider</th>
                      <th className="pb-3 text-sm font-medium text-gray-300">Instance</th>
                      <th className="pb-3 text-sm font-medium text-gray-300">Region</th>
                      <th className="pb-3 text-sm font-medium text-gray-300">Category</th>
                      <th className="pb-3 text-sm font-medium text-gray-300">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {failedLaunches.map((fl) => (
                      <React.Fragment key={fl.id}>
                        <tr
                          className="hover:bg-gray-800/30 cursor-pointer"
                          onClick={() => setExpandedFailure(expandedFailure === fl.id ? null : fl.id)}
                        >
                          <td className="py-3 text-sm text-gray-400">
                            {fl.created_at ? new Date(fl.created_at).toLocaleString() : '—'}
                          </td>
                          <td className="py-3">
                            <div className="text-sm text-white">{fl.user_name || fl.user_email}</div>
                            <div className="text-xs text-gray-500">ID: {fl.user_id}</div>
                          </td>
                          <td className="py-3">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                              fl.provider === 'aws' ? 'bg-orange-500/20 text-orange-400'
                              : fl.provider === 'gcp' ? 'bg-blue-500/20 text-blue-400'
                              : 'bg-purple-500/20 text-purple-400'
                            }`}>
                              {fl.provider?.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-3 text-sm text-gray-300 font-mono">{fl.instance_type}</td>
                          <td className="py-3 text-sm text-gray-400">{fl.region}</td>
                          <td className="py-3">
                            {fl.error_category && (
                              <span className={`inline-flex px-2 py-0.5 text-xs rounded-full ${
                                fl.error_category === 'capacity_exhausted' ? 'bg-amber-900/40 text-amber-300'
                                : fl.error_category === 'image_not_found' ? 'bg-purple-900/40 text-purple-300'
                                : fl.error_category === 'billing_error' ? 'bg-yellow-900/40 text-yellow-300'
                                : fl.error_category === 'network_error' ? 'bg-cyan-900/40 text-cyan-300'
                                : 'bg-gray-700/40 text-gray-300'
                              }`}>
                                {fl.error_category.replace(/_/g, ' ')}
                              </span>
                            )}
                          </td>
                          <td className="py-3 text-sm text-red-300 max-w-xs truncate">
                            {fl.user_error_explanation
                              ? fl.user_error_explanation.slice(0, 100) + (fl.user_error_explanation.length > 100 ? '...' : '')
                              : (fl.error_message || '—').slice(0, 100)}
                          </td>
                        </tr>
                        {expandedFailure === fl.id && (
                          <tr className="bg-red-950/20">
                            <td colSpan={7} className="px-4 py-4">
                              <div className="space-y-3">
                                {fl.user_error_explanation && (
                                  <div>
                                    <div className="text-xs font-medium text-gray-400 mb-1">AI Explanation</div>
                                    <p className="text-sm text-red-200">{fl.user_error_explanation}</p>
                                  </div>
                                )}
                                <div>
                                  <div className="text-xs font-medium text-gray-400 mb-1">Raw Error</div>
                                  <pre className="text-xs text-red-400/70 whitespace-pre-wrap break-all font-mono bg-gray-900/50 rounded p-2">
                                    {fl.error_message}
                                  </pre>
                                </div>
                                <div className="flex space-x-6 text-xs text-gray-500">
                                  <span>Instance ID: {fl.instance_id}</span>
                                  <span>Status: {fl.status}</span>
                                  {fl.total_cost > 0 && <span>Cost: ${fl.total_cost.toFixed(2)}</span>}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}